// Component: MonitoringTab.jsx
// Purpose: Real-time strategy monitoring with signal evaluation, position, and activity feed

import { useState, useEffect, useMemo } from 'react';
import SignalEvaluationPanel from './SignalEvaluationPanel';
import TradeHistory from '../../../components/trading/TradeHistory';
import { ActivityCard } from './SignalActivity';
import PerformanceMetrics from '../../../components/trading/PerformanceMetrics';
import { useLiveExecution, useLivePnL, useLevelHistory, usePositionPin, usePerformancePin } from '../../../contexts/TradingDataContext';
import StrategyService from '../../../api/services/strategies';
//* 50-21 R10-T5 — read-only display record (custom condition names): ONE GET per mount, zero PUTs.
import { useVisualOverridesRecord } from '../../../hooks/useVisualOverrides';

// strategyId is not used for live-execution subscription — the shared useLiveExecution hook keys
// purely off deploymentId. 50-21: it (else deployment.strategy_id) names the strategy whose display
// record (custom condition names) is loaded once, read-only, and handed to SignalEvaluationPanel.
const EMPTY_OMS_OUTCOMES = new Map();

const MonitoringTab = ({
  deployment,
  strategyInstanceId,
  strategyId,
  orders = [],
  positions = [],
  performance = [],
  omsOutcomes = EMPTY_OMS_OUTCOMES,
  rejectedSignalsUnavailable = false,
}) => {
  const [strategyConfig, setStrategyConfig] = useState({ nodes: [], edges: [] });
  //--> 50-21: the display record for this strategy (names only reach Monitoring; styling never does)
  const { overridesSaved: visualOverrides } = useVisualOverridesRecord(deployment?.strategy_id || strategyId);
  //--> Activity card tab ([Orders | Signals]).
  const [activityTab, setActivityTab] = useState('orders');
  //--> Live LTP tracking for monitoring page
  const { ltp: liveLtp } = useLivePnL(strategyInstanceId);


  //--> Live stop-loss / take-profit snapshots from trade_context stream
  const levelSnapshots = useLevelHistory(strategyInstanceId);
  const liveLevels = useMemo(() => {
    const latest = levelSnapshots?.length ? levelSnapshots[levelSnapshots.length - 1] : null;
    return {
      stopLoss: latest?.levels?.stop_loss?.price ?? null,
      takeProfit: latest?.levels?.take_profit?.price ?? null,
    };
  }, [levelSnapshots]);
  //--> Per-SII position/performance subscription pins (Spec 4) — mirror useLevelHistoryPin pattern
  usePositionPin(strategyInstanceId);
  usePerformancePin(strategyInstanceId);

  //--> Fetch strategy configuration (nodes/edges) to display static tree
  useEffect(() => {
    let active = true;
    const fetchConfig = async () => {
      if (!deployment?.strategy_id || !deployment?.version_id) return;
      try {
        const res = await StrategyService.getStrategyVersionById(deployment.strategy_id, deployment.version_id);
        const versionData = res?.data?.data || res?.data;
        if (active && versionData?.config) {
          setStrategyConfig(versionData.config);
        }
      } catch (err) {
        console.error('Failed to fetch strategy version config', err);
      }
    };
    fetchConfig();
    return () => { active = false; };
  }, [deployment?.strategy_id, deployment?.version_id]);

  //--> Derive position + performance from live data
  const position = positions[0] || null;
  const perf = performance[0] || null;

  //--> Subscribe to live strategy node evaluation results using full identity.
  //! Board #73 — engineDecision destructured from the SAME hook call as
  //!  nodeResults (never a separate call) so the two always describe the same
  //!  bar, and forwarded to SignalEvaluationPanel below.
  //! One shared, ref-counted WS subscription per deployment on the Provider (LiveAnalysisTab
  //!  reads the same entry) — the legacy useStrategyExecution shim was deleted (10502-5).
  //!  First value on a fresh page comes from the bridge's retained replay (strategy-worker
  //!  `strategy:execution:latest:{deployment_id}`), not from waiting for the next bar close.
  const { liveNodeResults: nodeResults, engineDecision } = useLiveExecution(deployment?.deployment_id);

  return (
    <div className="space-y-5">
      {/* --> Stats row: 6 metric boxes with live P&L */}
      <PerformanceMetrics
        performance={perf}
        position={position}
        strategyInstanceId={strategyInstanceId}
      />

      {/* --> Monitoring centre + activity rail. The rail sits beside the trees only at xl (>=1280);
           below that the trees take the full width and the activity card stacks underneath. */}
      <div className="grid grid-cols-1 gap-5 xl:grid-cols-3">
        <div className="space-y-4 xl:col-span-2">
          {/*>>> POSITION + SIGNAL PANEL — auto-switches entry/exit based on position state <<<*/}
          <SignalEvaluationPanel
            position={position}
            isScanning
            liveLtp={liveLtp}
            strategyConfig={strategyConfig}
            nodeResults={nodeResults}
            engineDecision={engineDecision}
            orders={orders}
            liveLevels={liveLevels}
            omsOutcomes={omsOutcomes}
            visualOverrides={visualOverrides}
          />
        </div>

        {/*>>> ACTIVITY — [Orders | Signals] card; sticky with its own scroll beside the trees <<<*/}
        <div className="xl:sticky xl:top-4 xl:self-start xl:max-h-[calc(100dvh-2rem)] xl:overflow-y-auto">
          <ActivityCard
            tab={activityTab}
            onTab={setActivityTab}
            ordersView={<TradeHistory orders={orders} positions={positions} mode="compact" maxTrades={5} />}
            omsOutcomes={omsOutcomes}
            unavailable={rejectedSignalsUnavailable}
            deploymentMode={deployment?.mode}
          />
        </div>
      </div>
    </div>
  );
};

export default MonitoringTab;
